import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { CustomButton, Text } from '../../shared/uiComponents';
import { TEXT as TEXT_COLORS } from '../../shared/Helpers/colors';
import { errorNormalizer, getInitials } from '../../shared/Helpers/functions';

const AVATAR_COLORS = ['#2E7D6F', '#E67E22', '#3498DB', '#9B59B6', '#E74C3C', '#1ABC9C', '#F39C12', '#8E44AD'];
const getAvatarColor = (id: string) => {
	let hash = 0;
	for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
	return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

import { DispatchProperties, useSelector } from '../../redux/store';
import { approveCancellationGroup, denyCancellationGroup } from '../../redux/State/clientSlice/scheduleCancellationSlice';
import {
	CancellingParty,
	PendingCancellationRequestViewModel,
	RescheduleIntent,
} from '../../redux/API/ClientAPIHelpers/scheduleCancellation';

const cancellingPartyLabels: Record<CancellingParty, string> = {
	[CancellingParty.Provider]: 'BT (Provider)',
	[CancellingParty.ClientFamily]: 'Client/Family',
	[CancellingParty.Agency]: 'Agency',
};

const rescheduleIntentLabels: Record<RescheduleIntent, string> = {
	[RescheduleIntent.Yes]: 'Yes - will coordinate a make-up session',
	[RescheduleIntent.No]: 'No - this session will not be made up',
	[RescheduleIntent.NotSure]: 'Not sure yet',
};

interface PendingRequestsModalProps {
	onClose: () => void;
}

const DetailItem: FC<{ label: string; value: string }> = ({ label, value }) => (
	<div className="pendingRequestDetailItem">
		<Text title={label} type="psm" fontWeight="semiBold" />
		<Text title={value} type="psm" />
	</div>
);

const RequestCard: FC<{ request: PendingCancellationRequestViewModel }> = ({ request }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [status, setStatus] = useState<'pending' | 'approved' | 'denied'>('pending');
	const [loading, setLoading] = useState<'approve' | 'deny' | null>(null);

	const handleApprove = () => {
		setLoading('approve');
		dispatch(approveCancellationGroup(request.groupId))
			.then(unwrapResult)
			.then(() => setStatus('approved'))
			.catch(errorNormalizer)
			.finally(() => setLoading(null));
	};

	const handleDeny = () => {
		setLoading('deny');
		dispatch(denyCancellationGroup({ groupId: request.groupId }))
			.then(unwrapResult)
			.then(() => setStatus('denied'))
			.catch(errorNormalizer)
			.finally(() => setLoading(null));
	};

	const clientCount = request.affectedClients.length;
	const clientsLabel = request.allClients
		? clientCount > 0 ? `All clients (${clientCount})` : 'All clients'
		: clientCount > 0 ? request.affectedClients.map((c) => c.clientName).join(', ') : 'Specific clients';

	const avatarColor = getAvatarColor(request.providerId);

	return (
		<div className="pendingRequestCard">
			<div className="pendingRequestHeader">
				<div className="pendingRequestProvider">
					<div className="providerAvatar" style={{ background: avatarColor, color: '#fff' }}>
						{getInitials(request.providerName)}
					</div>
					<div>
						<Text title={request.providerName} type="pmd" fontWeight="semiBold" />
						<Text
							title={`Submitted ${dayjs(request.createdAt).format('MMMM D, YYYY')}`}
							type="pxs"
							color={TEXT_COLORS.TERTIARY}
						/>
					</div>
				</div>
				<div className="pendingRequestActions">
					{status === 'pending' && (
						<>
							<CustomButton title="Approve" buttonType="primary" onClick={handleApprove} loading={loading === 'approve'} disabled={loading !== null} />
							<CustomButton
								title="Deny"
								buttonType="tertiary"
								onClick={handleDeny}
								loading={loading === 'deny'}
								disabled={loading !== null}
								style={{ color: '#ef4444', borderColor: '#ef4444', borderWidth: '1.5px', borderStyle: 'solid' }}
							/>
						</>
					)}
					{status === 'approved' && (
						<Text title="✓ Approved" type="psm" fontWeight="semiBold" color="#2E7D6F" />
					)}
					{status === 'denied' && (
						<Text title="✗ Denied" type="psm" fontWeight="semiBold" color="#ef4444" />
					)}
				</div>
			</div>
			<div className="pendingRequestDetailGrid">
				<DetailItem label="Dates:" value={`${dayjs(request.startDate).format('MMMM D')} \u2013 ${dayjs(request.endDate).format('MMMM D, YYYY')}`} />
				<DetailItem
					label="Cancelling party:"
					value={request.cancellingParty !== null ? cancellingPartyLabels[request.cancellingParty] : 'Unknown'}
				/>
				<DetailItem label="Reason:" value={request.reasonName || 'Unknown'} />
				<DetailItem
					label="Reschedule intent:"
					value={request.rescheduleIntent !== null ? rescheduleIntentLabels[request.rescheduleIntent] : 'Not specified'}
				/>
				<DetailItem label="Clients affected:" value={clientsLabel} />
				<DetailItem label="Sessions affected:" value={`${request.affectedSessionCount ?? '—'} sessions`} />
			</div>
			{request.notes && (
				<div className="pendingRequestNotes">
					<Text title={`"${request.notes}"`} type="pmd" color={TEXT_COLORS.TERTIARY} className="pendingRequestNotesText" />
				</div>
			)}
		</div>
	);
};

const PendingRequestsModal: FC<PendingRequestsModalProps> = ({ onClose }) => {
	const reduxRequests = useSelector((state) => state.scheduleCancellation.pendingRequests);
	const loading = useSelector((state) => state.scheduleCancellation.pendingRequestsLoading);
	const [localRequests] = useState(reduxRequests);

	return (
		<div className="pendingRequestsModal">
			{loading && <Text title="Loading..." type="psm" color={TEXT_COLORS.TERTIARY} />}
			{!loading && localRequests.length === 0 && (
				<Text title="No pending requests" type="psm" color={TEXT_COLORS.TERTIARY} />
			)}
			{localRequests.map((request) => (
				<RequestCard key={request.groupId} request={request} />
			))}
			<div className="pendingRequestsFooter">
				<CustomButton title="Close" buttonType="tertiary" onClick={onClose} />
			</div>
		</div>
	);
};

export default PendingRequestsModal;
